/* ============================================================
   artvision® · PREISVORSCHLAG (Werkseite)
   Steuert snippets/av-offer-button.liquid und av-offer-dialog.liquid.
   · Nicht angemeldet → Collector Login, danach öffnet sich das
     Fenster von selbst (Resume-after-Auth, Aktion 'offer').
   · Angemeldet → Preisrahmen (GET /artworks?ids=) und eigene
     Vorschläge zu diesem Werk (GET /offers?artwork=) laden. Läuft
     schon einer oder ist der Preis vereinbart, steht das statt des
     Formulars da (mit „Jetzt bezahlen“, sobald der Link bereit ist).
   · Senden → POST /offers. Unter dem geheimen Mindestpreis lehnt das
     Backend sofort ab: „Leider zu niedrig“.
   · Natives <dialog> mit showModal(): Seite dahinter inert, Tab bleibt
     im Fenster, Esc schließt, der Fokus kehrt zum Knopf zurück.
   Backend: posh-vision-backend, docs/adr/0006_artwork_offers.md.
   ============================================================ */

import {
  getCurrentCollector,
  isCollectorLoginEnabled,
  collectorConfig,
  openDashboard,
  registerPendingActionHandler,
  requestCollectorAuth,
  resendVerificationEmail,
  waitForCollector,
  wasSessionExpired
} from 'av-collector-session';
import { createOfferAction, normalizeShopifyId } from 'av-collector-core';
import {
  OFFER_CHANCE_EMPTY_LABEL,
  OFFER_CHANCE_LABELS,
  OFFER_TEXTS,
  OfferApiError,
  buildArtworkOffersUrl,
  buildArtworkUrl,
  buildOfferStatusUrl,
  buildCreateOfferUrl,
  formatDeadline,
  formatEuro,
  isDeclinedAtOnce,
  offerAmountProblem,
  offerChance,
  offerErrorMessage,
  parseOfferAmount,
  readApiErrorCode,
  readExistingOffer,
  readOfferTerms,
  readOffersPossible
} from 'av-offer-core';

const FOCUSABLE_SELECTOR = 'a[href]:not([hidden]), button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const SCROLL_LOCK_CLASS = 'av-offer-is-open';
// Generous: a cold backend (serverless database waking up, local `php artisan serve`) can take a while.
const REQUEST_TIMEOUT_MS = 25000;
const HTTP_UNAUTHORIZED = 401;
const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';
const VERIFY_TEXT = 'Bitte klicken Sie zuerst auf den Link in unserer E-Mail. Danach geht es hier weiter.';
const VERIFY_SENT_TEXT = 'E-Mail ist unterwegs. Bitte auch im Spam-Ordner nachsehen.';
const ALREADY_OPEN = 'OFFER_ALREADY_OPEN';

/** One dialog per artwork page; keyed by product id in case a section renders twice. */
function dialogFor(productId) {
  return document.querySelector(`[data-av-offer-dialog][data-product-id="${productId}"]`);
}

const state = new WeakMap(); // dialog → { productId, terms, returnFocusTo, loadToken }

function stateOf(dialog) {
  if (!state.has(dialog)) state.set(dialog, { productId: dialog.dataset.productId, terms: null, returnFocusTo: null, loadToken: 0 });
  return state.get(dialog);
}

/* ---------- API -------------------------------------------- */

async function idToken(user) {
  // A freshly confirmed address only reaches the token after a reload and a refresh.
  const wasVerified = user.emailVerified;
  if (!wasVerified) {
    try {
      await user.reload();
    } catch (error) {
      console.error('Offer: collector could not be reloaded', error);
    }
  }
  return user.getIdToken(!wasVerified && user.emailVerified);
}

async function request(url, { method = 'GET', token, body } = {}) {
  if (!url) throw new OfferApiError(null, 0);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'omit',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch (error) {
    throw new OfferApiError(null, 0, { cause: error });
  } finally {
    window.clearTimeout(timeout);
  }

  let json = null;
  try {
    json = await response.json();
  } catch {
    // Empty or non-JSON body; the status decides.
  }

  if (!response.ok) throw new OfferApiError(readApiErrorCode(json), response.status);
  return json;
}

/* ---------- Ansichten -------------------------------------- */

function showView(dialog, name) {
  let view = null;
  dialog.querySelectorAll('[data-av-offer-view]').forEach((element) => {
    const isActive = element.dataset.avOfferView === name;
    element.hidden = !isActive;
    if (isActive) view = element;
  });
  if (!view) return;

  const title = view.querySelector('[data-av-offer-title]');
  dialog.setAttribute('aria-labelledby', title.id);

  const input = view.querySelector('[data-av-offer-amount]');
  (input || title).focus();
}

/**
 * Message instead of the form. Every button is hidden unless asked for:
 * { pay: url } shows "Jetzt bezahlen", { retry: true } "Höheren Betrag
 * vorschlagen", { dashboard: true } the link to the collector's offers.
 */
function showMessage(dialog, { title, text, pay = null, retry = false, dashboard = false, verify = false }) {
  dialog.querySelector('[data-av-offer-message-title]').textContent = title;
  dialog.querySelector('[data-av-offer-message-text]').textContent = text;

  const view = dialog.querySelector('[data-av-offer-view="message"]');
  const payLink = view.querySelector('[data-av-offer-pay]');
  payLink.hidden = !pay;
  if (pay) payLink.href = pay;
  view.querySelector('[data-av-offer-retry]').hidden = !retry;
  view.querySelector('[data-av-offer-dashboard]').hidden = !dashboard;
  view.querySelector('[data-av-offer-verify-check]').hidden = !verify;
  view.querySelector('[data-av-offer-verify-send]').hidden = !verify;

  showView(dialog, 'message');
}

/** Unconfirmed address: confirm by mail, then try again here — no dead end. */
function showVerify(dialog, text = VERIFY_TEXT) {
  showMessage(dialog, { title: 'E-Mail bestätigen', text, verify: true });
}

function showError(dialog, error) {
  if (error && error.code === EMAIL_NOT_VERIFIED) {
    showVerify(dialog);
    return;
  }
  showMessage(dialog, { title: 'Ihr Preisvorschlag', text: offerErrorMessage(error && error.code) });
}

function showExisting(dialog, existing) {
  if (existing.kind === 'pay') {
    showMessage(dialog, {
      title: OFFER_TEXTS.agreedTitle,
      text: OFFER_TEXTS.payText(existing.amount, formatDeadline(existing.payUntil)),
      pay: existing.url,
      dashboard: true
    });
  } else if (existing.kind === 'agreed') {
    showMessage(dialog, {
      title: OFFER_TEXTS.agreedTitle,
      text: `${OFFER_TEXTS.payText(existing.amount, '')} ${OFFER_TEXTS.preparingText}`,
      dashboard: true
    });
  } else {
    showMessage(dialog, { title: 'Ihr Preisvorschlag', text: OFFER_TEXTS.openText(existing.amount), dashboard: true });
  }
}

/* ---------- Formular --------------------------------------- */

function setNotice(dialog, message) {
  const notice = dialog.querySelector('[data-av-offer-notice]');
  notice.textContent = message || '';
  notice.hidden = !message;
}

function setFieldError(dialog, message) {
  const error = dialog.querySelector('[data-av-offer-error]');
  const input = dialog.querySelector('[data-av-offer-amount]');
  error.textContent = message || '';
  error.hidden = !message;
  input.setAttribute('aria-invalid', String(Boolean(message)));
}

function renderAmount(dialog) {
  const { terms } = stateOf(dialog);
  if (!terms) return;

  const input = dialog.querySelector('[data-av-offer-amount]');
  // Grows with what was typed, so amount and "€" stay centred (placeholder "0" when empty).
  input.style.width = `${Math.max(input.value.length, 1) + 0.5}ch`;

  const amount = parseOfferAmount(input.value);
  const chance = offerChance(amount, terms.listPrice);

  const meter = dialog.querySelector('[data-av-offer-chance]');
  meter.dataset.chance = chance || '';
  dialog.querySelector('[data-av-offer-chance-text]').textContent = chance ? OFFER_CHANCE_LABELS[chance] : OFFER_CHANCE_EMPTY_LABEL;
  dialog.querySelector('[data-av-offer-total]').textContent = amount ? formatEuro(amount + terms.shippingCost) : '–';
}

function prepareForm(dialog, terms) {
  stateOf(dialog).terms = terms;

  dialog.querySelector('[data-av-offer-list-price]').textContent = formatEuro(terms.listPrice);
  dialog.querySelector('[data-av-offer-shipping]').textContent =
    terms.shippingCost > 0 ? `+ Versand ${formatEuro(terms.shippingCost)}` : 'Versand kostenlos';
  dialog.querySelector('[data-av-offer-range]').textContent = `Zwischen ${formatEuro(terms.min)} und ${formatEuro(terms.max)}`;

  dialog.querySelector('[data-av-offer-form]').reset();
  setNotice(dialog, '');
  setFieldError(dialog, '');
  renderAmount(dialog);
  showView(dialog, 'form');
}

async function submitOffer(dialog) {
  const { productId, terms } = stateOf(dialog);
  const input = dialog.querySelector('[data-av-offer-amount]');
  const problem = offerAmountProblem(input.value, terms);

  if (problem) {
    setFieldError(dialog, OFFER_TEXTS.amountProblem(terms));
    input.focus();
    return;
  }
  setFieldError(dialog, '');
  setNotice(dialog, '');

  const user = await waitForCollector();
  if (!user) {
    askForLogin(dialog, productId);
    return;
  }

  const amount = parseOfferAmount(input.value);
  let body;
  try {
    body = await request(buildCreateOfferUrl(collectorConfig.apiBaseUrl), {
      method: 'POST',
      token: await idToken(user),
      body: { artwork_id: productId, amount }
    });
  } catch (error) {
    handleFormError(dialog, error, productId);
    return;
  }

  const offer = body && body.data;
  if (isDeclinedAtOnce(offer)) {
    showMessage(dialog, { title: OFFER_TEXTS.tooLowTitle, text: OFFER_TEXTS.tooLowText(amount), retry: true });
    return;
  }

  dialog.querySelector('[data-av-offer-sent-text]').textContent = OFFER_TEXTS.sentText(amount);
  const steps = dialog.querySelector('[data-av-offer-steps]');
  steps.replaceChildren(
    ...OFFER_TEXTS.nextSteps.map((text) => {
      const item = document.createElement('li');
      item.textContent = text;
      return item;
    })
  );
  showView(dialog, 'sent');
}

function handleFormError(dialog, error, productId) {
  console.error('Offer could not be sent', error);

  if (error.status === HTTP_UNAUTHORIZED) {
    askForLogin(dialog, productId);
  } else if (error.code === ALREADY_OPEN) {
    void loadDialog(dialog);
  } else if (error.code === EMAIL_NOT_VERIFIED) {
    showVerify(dialog);
  } else {
    setNotice(dialog, offerErrorMessage(error.code));
  }
}

/* ---------- Laden ------------------------------------------ */

async function loadDialog(dialog) {
  const current = stateOf(dialog);
  const token = ++current.loadToken;
  current.terms = null;
  showView(dialog, 'loading');

  const user = await waitForCollector();
  if (!user) {
    askForLogin(dialog, current.productId);
    return;
  }

  let terms;
  let existing;
  try {
    const bearer = await idToken(user);
    const [artworkBody, offersBody] = await Promise.all([
      request(buildArtworkUrl(collectorConfig.apiBaseUrl, current.productId), { token: bearer }),
      request(buildArtworkOffersUrl(collectorConfig.apiBaseUrl, current.productId), { token: bearer })
    ]);
    terms = readOfferTerms(artworkBody);
    existing = readExistingOffer(offersBody);
  } catch (error) {
    if (token !== current.loadToken || !dialog.open) return;
    console.error('Offer: data could not be loaded', error);

    if (error.status === HTTP_UNAUTHORIZED) {
      askForLogin(dialog, current.productId);
    } else {
      showError(dialog, error);
    }
    return;
  }

  // Closed or reopened meanwhile: this answer is stale.
  if (token !== current.loadToken || !dialog.open) return;

  if (existing) {
    showExisting(dialog, existing);
  } else if (!terms) {
    showMessage(dialog, { title: 'Ihr Preisvorschlag', text: OFFER_TEXTS.notPossible });
  } else {
    prepareForm(dialog, terms);
  }
}

/* ---------- Öffnen und Schließen --------------------------- */

function openDialog(dialog) {
  if (!dialog.open) {
    const active = document.activeElement;
    stateOf(dialog).returnFocusTo =
      active && active !== document.body ? active : document.querySelector(`[data-av-offer-open][data-product-id="${dialog.dataset.productId}"]`);
    dialog.showModal();
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  }
  void loadDialog(dialog);
}

function closeDialog(dialog) {
  if (dialog.open) dialog.close();
}

function onDialogClosed(dialog) {
  const current = stateOf(dialog);
  current.loadToken += 1; // answers still on the way are dropped
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

  const target = current.returnFocusTo;
  current.returnFocusTo = null;
  if (target && target.isConnected) target.focus();
}

/** Session gone or never there: the auth layer takes over and reopens this dialog afterwards. */
function askForLogin(dialog, productId) {
  closeDialog(dialog);
  requestCollectorAuth({
    view: wasSessionExpired() || getCurrentCollector() ? 'session_expired' : 'sign_in',
    pendingAction: createOfferAction(productId)
  });
}

function trapFocus(dialog, event) {
  const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => !element.closest('[hidden]'));
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

async function withBusy(button, task) {
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  try {
    await task();
  } catch (error) {
    console.error('Offer action failed', error);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/* ---------- Ereignisse ------------------------------------- */

async function onOpenClick(button) {
  const productId = normalizeShopifyId(button.dataset.productId);
  const dialog = productId ? dialogFor(productId) : null;
  if (!dialog) {
    console.error('Offer button without its dialog (snippets/av-offer-dialog.liquid)', button);
    return;
  }

  const user = await waitForCollector();
  if (!user) {
    requestCollectorAuth({
      view: wasSessionExpired() ? 'session_expired' : 'sign_in',
      pendingAction: createOfferAction(productId)
    });
    return;
  }
  openDialog(dialog);
}

function wireDialog(dialog) {
  if (dialog.dataset.avOfferWired === 'true') return;
  dialog.dataset.avOfferWired = 'true';

  dialog.addEventListener('close', () => onDialogClosed(dialog));
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') trapFocus(dialog, event);
  });
  dialog.addEventListener('input', (event) => {
    if (event.target.matches('[data-av-offer-amount]')) {
      setFieldError(dialog, '');
      renderAmount(dialog);
    }
  });
  dialog.addEventListener('submit', (event) => {
    event.preventDefault();
    const button = dialog.querySelector('[data-av-offer-submit]');
    void withBusy(button, () => submitOffer(dialog));
  });
  dialog.addEventListener('click', (event) => {
    // Clicks outside the panel land on the <dialog> itself, which is the dimmed backdrop.
    if (event.target === dialog || event.target.closest('[data-av-offer-close]')) {
      closeDialog(dialog);
      return;
    }

    if (event.target.closest('[data-av-offer-retry]')) {
      const { terms } = stateOf(dialog);
      if (terms) {
        prepareForm(dialog, terms);
      } else {
        void loadDialog(dialog);
      }
      return;
    }

    if (event.target.closest('[data-av-offer-verify-check]')) {
      void loadDialog(dialog);
      return;
    }

    const sendButton = event.target.closest('[data-av-offer-verify-send]');
    if (sendButton) {
      void withBusy(sendButton, async () => {
        try {
          await resendVerificationEmail();
          showVerify(dialog, VERIFY_SENT_TEXT);
        } catch (error) {
          console.error('Offer: verification e-mail could not be sent', error);
          showVerify(dialog, 'Das hat nicht geklappt. Bitte in ein paar Minuten nochmal versuchen.');
        }
      });
      return;
    }

    const dashboardButton = event.target.closest('[data-av-offer-dashboard]');
    if (dashboardButton) void withBusy(dashboardButton, () => openDashboard('offers'));
  });
}

/**
 * The button starts hidden (snippets/av-offer-button.liquid) and only
 * appears when the backend says the artwork takes offers right now — Liquid
 * cannot know everything (switched off, price already agreed, not in the
 * backend). On any error it stays hidden.
 */
async function revealIfOffersPossible(button) {
  const url = buildOfferStatusUrl(collectorConfig.apiBaseUrl, button.dataset.productId);
  if (!url) return;

  try {
    const response = await fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } });
    if (response.ok && readOffersPossible(await response.json())) button.hidden = false;
  } catch (error) {
    console.error('Offer: availability could not be checked', error);
  }
}

function start() {
  const buttons = document.querySelectorAll('[data-av-offer-open]');

  if (!isCollectorLoginEnabled) {
    buttons.forEach((button) => {
      button.hidden = true;
    });
    return;
  }

  document.querySelectorAll('[data-av-offer-dialog]').forEach(wireDialog);
  buttons.forEach((button) => void revealIfOffersPossible(button));

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-av-offer-open]');
    if (!button) return;
    event.preventDefault();
    void withBusy(button, () => onOpenClick(button));
  });

  registerPendingActionHandler('offer', (action) => {
    const dialog = dialogFor(action.productId);
    if (dialog) openDialog(dialog);
  });

  // Sections re-rendered in the theme editor bring a fresh dialog.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-av-offer-dialog]').forEach(wireDialog);
    event.target.querySelectorAll('[data-av-offer-open]').forEach((button) => void revealIfOffersPossible(button));
  });
}

start();
